import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '@/components/auth';
import { UserAvatar, HelpModal } from '@/components/common';
import { EnvironmentToggle } from '@/components/common/EnvironmentToggle';

const navItems = [
  { to: '/', icon: '🏠', label: 'Home', end: true },
  { to: '/agencies', icon: '🏢', label: 'Agenturen', end: false },
  { to: '/dashboard', icon: '📊', label: 'Dashboard', end: true },
];

export function Header(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, getUser } = useAuth();
  const user = getUser();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Hide mobile menu in rating flow
  const isRatingFlow = location.pathname.startsWith('/rating');

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setShowMobileMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close open menus on Escape key
  useEffect(() => {
    if (!showUserMenu && !showMobileMenu) return;
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
        setShowMobileMenu(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showUserMenu, showMobileMenu]);

  // Close mobile menu on route change - using useLayoutEffect to run before paint
  useEffect(() => {
    // Close menu when location changes (but not on initial mount)
    return () => {
      setShowMobileMenu(false);
    };
  }, [location.pathname]);

  const handleLogout = async (): Promise<void> => {
    setShowUserMenu(false);
    await logout();
  };

  return (
    <header className="bg-white/95 backdrop-blur-md border-b border-charcoal-100 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] py-3 sticky top-0 z-50 pt-[max(0.75rem,env(safe-area-inset-top))]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Mobile Menu Toggle - only visible on small screens and not in rating flow */}
          {!isRatingFlow && (
            <div ref={mobileMenuRef} className="sm:hidden relative">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="min-w-11 min-h-11 flex items-center justify-center text-charcoal-600 hover:bg-charcoal-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2"
                aria-label="Menü öffnen"
                aria-expanded={showMobileMenu}
              >
                {showMobileMenu ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>

              {/* Mobile Menu Dropdown */}
              {showMobileMenu && (
                <div className="absolute left-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-charcoal-100 overflow-hidden z-50">
                  <nav className="py-2">
                    {navItems.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-cherry-50 text-cherry-700'
                              : 'text-charcoal-700 hover:bg-charcoal-50'
                          }`
                        }
                      >
                        <span className="text-lg">{item.icon}</span>
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </nav>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="flex items-center gap-2 p-0 m-0 bg-transparent border-0 text-left focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2 rounded-xl"
            onClick={() => { void navigate('/'); }}
            aria-label="Zur Startseite"
          >
            <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-sm border border-charcoal-100">
              <span className="text-2xl">🍒</span>
            </div>
            <div>
              <h1 className="font-bold text-lg text-charcoal-900 leading-tight">
                CherryPulse
              </h1>
              <p className="text-xs text-charcoal-500 hidden sm:block leading-tight">
                Agentur-Qualität messbar machen
              </p>
            </div>
          </button>
        </div>
        <div className="flex items-center gap-3 relative" ref={menuRef}>
          {/* Help Button */}
          <button
            onClick={() => setShowHelp(true)}
            className="min-w-11 min-h-11 flex items-center justify-center text-charcoal-500 hover:text-charcoal-700 hover:bg-charcoal-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2"
            aria-label="Hilfe öffnen"
            title="Hilfe & Handbuch"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>

          {/* Environment Toggle */}
          <EnvironmentToggle />

          {user && (
            <>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="hover:opacity-80 transition-opacity duration-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2"
                title={user.displayName}
                aria-label="Benutzermenü"
                aria-expanded={showUserMenu}
              >
                <UserAvatar
                  avatar={user.avatar}
                  displayName={user.displayName}
                  size="md"
                />
              </button>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-charcoal-100 overflow-hidden z-50">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-charcoal-100">
                    <p className="font-medium text-charcoal-900 truncate">
                      {user.displayName}
                    </p>
                    <p className="text-sm text-charcoal-500 truncate">
                      {user.email}
                    </p>
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        setShowHelp(true);
                      }}
                      className="w-full min-h-11 px-4 py-2 text-left text-sm text-charcoal-700 hover:bg-charcoal-50 flex items-center gap-2 transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Hilfe & Handbuch
                    </button>
                    <button
                      onClick={() => void handleLogout()}
                      className="w-full min-h-11 px-4 py-2 text-left text-sm text-charcoal-700 hover:bg-charcoal-50 flex items-center gap-2 transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Abmelden
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Help Modal */}
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </header>
  );
}
